import type { CSSProperties } from 'react';
import { Link } from 'react-router';
import { useI18nContext } from '@/i18n/i18n-react';
import type { Locales } from '@/i18n/i18n-types';
import type { CampaignDto } from '@/generated/korza-api-client/types.gen';
import { Calendar, ChevronLeft, Link2, TrendingUp } from 'lucide-react';
import {
  CenteredContent,
  CenteredContentLayout,
  Header,
} from '@/layouts/centered-content.layout';
import { SidebarLayout } from '@/layouts/sidebar.layout';
import { PageBreadcrumb } from '@/components/page-breadcrumb';
import AudienceDetailsSection from '@/components/audiences/audience-details-section';
import { CampaignAnalyticsEmptyState } from '@/components/campaigns/campaign-analytics-empty-state';
import { CampaignAnalyticsWarnings } from '@/components/campaign-analytics-warnings';
import { CampaignResultsSection } from '@/components/campaigns/campaign-results-section';
import { CampaignLabelsIcon } from '@/components/campaign-labels-icon';
import { CampaignAudienceCard } from '@/components/campaigns/campaign-audience-card';
import { CampaignDetailsActionBar } from '@/components/campaigns/campaign-details-action-bar';
import { CampaignDetailsHeaderActions } from '@/components/campaigns/campaign-details-header-actions';
import { CampaignStatusBadge } from '@/components/campaigns/campaign-status-badge';
import { PushPreview } from '@/components/campaign-composer/push-preview';
import { useIsMobile } from '@/hooks/use-mobile';
import { canCancelCampaign } from '@/lib/campaign-list';
import { CampaignNotificationSection } from '@/components/campaigns/campaign-notification-section';
import { InlineCode } from '@/components/inline-code';
import { Badge } from '@/components/ui/badge';
import type { StoreCampaignAverages } from '@/hooks/use-store-campaign-averages';
import { formatShortDateTime } from '@/lib/utils';

function entryDelay(multiplier: number): CSSProperties {
  return {
    animationDelay: `calc(var(--entry-delay-base)/4 * ${multiplier})`,
  };
}

interface CampaignDetailsPresenterProps {
  campaign: CampaignDto;
  tenantSlug: string;
  tenantIconUrl: string | null | undefined;
  tenantName: string;
  locale: Locales;
  /** `undefined` while the store currency loads: money shows a skeleton. */
  currency: string | undefined;
  /** Store averages of the last 90 days (CAMP-03); `null` = no labels. */
  storeAverages: StoreCampaignAverages | null;
  analyticsEnabled: boolean;
  audiencesEnabled: boolean;
  isLaunchInFuture: boolean;
  isCancelling: boolean;
  onCancelConfirm: () => void;
}

export function CampaignDetailsPresenter({
  campaign,
  tenantSlug,
  tenantIconUrl,
  tenantName,
  locale,
  currency,
  storeAverages,
  analyticsEnabled,
  audiencesEnabled,
  isLaunchInFuture,
  isCancelling,
  onCancelConfirm,
}: CampaignDetailsPresenterProps) {
  const { LL } = useI18nContext();
  const isMobile = useIsMobile();
  // PUSH-10 AC 3: by status (same rule as the list and the backend), not date.
  const canCancel = canCancelCampaign(campaign);
  const duplicateHref = `/${tenantSlug}/campaigns/create?from=${campaign.id}`;

  const showAnalyticsMetrics = analyticsEnabled && !isLaunchInFuture;

  // Top → bottom: date(0) → analytics → audience → notification → tracking
  const audienceDelay = analyticsEnabled ? (showAnalyticsMetrics ? 7 : 2) : 1;
  const notificationDelay = audiencesEnabled
    ? audienceDelay + 1
    : analyticsEnabled
      ? showAnalyticsMetrics
        ? 7
        : 2
      : 1;
  const trackingDelay = notificationDelay + 1;

  const audienceCard = audiencesEnabled ? (
    <CampaignAudienceCard
      audience={campaign.audience}
      tenantSlug={tenantSlug}
      className="entry-fade-in-up"
      style={entryDelay(audienceDelay)}
    />
  ) : null;

  return (
    <SidebarLayout
      className="!p-0"
      mobileHeader={{
        title: campaign.name,
        backTo: `/${tenantSlug}/campaigns`,
      }}
    >
      <CenteredContentLayout>
        <Header
          className="flex justify-between w-full max-md:hidden"
          rightGutter
        >
          <div className="flex min-w-0 flex-col gap-2">
            <PageBreadcrumb
              items={[
                {
                  label: LL.campaigns.title(),
                  href: `/${tenantSlug}/campaigns`,
                },
                { label: LL.campaignDetails.breadcrumbLabel() },
              ]}
            />
            <div className="flex min-w-0 items-center justify-between gap-4">
              <div className="flex min-w-0 items-center gap-2 sm:gap-4">
                <Link
                  to={`/${tenantSlug}/campaigns`}
                  className="shrink-0"
                  aria-label={LL.campaigns.title()}
                >
                  <ChevronLeft className="size-6" />
                </Link>
                <h1 className="min-w-0 truncate text-lg tracking-tight sm:text-xl">
                  {campaign.name}
                </h1>
              </div>
            </div>
          </div>
          <CampaignDetailsHeaderActions
            campaignName={campaign.name}
            duplicateHref={duplicateHref}
            canCancel={canCancel}
            isCancelling={isCancelling}
            onCancelConfirm={onCancelConfirm}
          />
        </Header>

        <CenteredContent>
          <div className="flex h-full min-w-0 flex-col gap-8 pb-8 max-md:min-h-full max-md:pb-0 max-md:pt-4 sm:gap-10 sm:pb-12">
            <div
              className="flex flex-col gap-1 entry-fade-in"
              style={entryDelay(0)}
            >
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="size-4 shrink-0" />
                <span>{LL.campaignDetails.launchDate()}</span>
                {isMobile ? (
                  <CampaignStatusBadge status={campaign.status} />
                ) : (
                  isLaunchInFuture && (
                    <Badge variant="secondary" className="shrink-0 font-normal">
                      {LL.campaigns.scheduled()}
                    </Badge>
                  )
                )}
              </div>
              <p className="text-sm font-medium text-primary">
                {formatShortDateTime(campaign.launchDateTime, locale)}
              </p>
            </div>

            {isMobile && (
              <PushPreview
                title={campaign.notificationTitle}
                body={campaign.notificationBody}
                iconUrl={tenantIconUrl}
                appName={tenantName}
                locale={locale}
              />
            )}

            {analyticsEnabled && (
              <AudienceDetailsSection
                icon={TrendingUp}
                title={
                  showAnalyticsMetrics
                    ? LL.campaignDetails.results.title()
                    : LL.campaignDetails.analytics()
                }
                titleAside={
                  showAnalyticsMetrics || campaign.labels.length > 0 ? (
                    <>
                      {showAnalyticsMetrics && (
                        <CampaignAnalyticsWarnings
                          analytics={campaign.analytics}
                        />
                      )}
                      <CampaignLabelsIcon labels={campaign.labels} />
                    </>
                  ) : undefined
                }
                description={
                  showAnalyticsMetrics
                    ? LL.campaignDetails.results.description()
                    : LL.campaignDetails.analyticsDescription()
                }
                className="entry-fade-in"
                style={entryDelay(1)}
              >
                <div className="flex flex-col gap-4">
                  {showAnalyticsMetrics ? (
                    <div className="entry-fade-in-up" style={entryDelay(1)}>
                      <CampaignResultsSection
                        analytics={campaign.analytics}
                        averages={storeAverages}
                        currency={currency}
                        compact={isMobile}
                      />
                    </div>
                  ) : (
                    <CampaignAnalyticsEmptyState
                      className="entry-fade-in-up"
                      style={entryDelay(2)}
                    />
                  )}
                  {audienceCard}
                </div>
              </AudienceDetailsSection>
            )}

            {!analyticsEnabled && audienceCard}

            {!isMobile && (
              <CampaignNotificationSection
                className="entry-fade-in-up"
                style={entryDelay(notificationDelay)}
                notificationTitle={campaign.notificationTitle}
                notificationBody={campaign.notificationBody}
                notificationLink={campaign.notificationLink}
                iconUrl={tenantIconUrl}
              />
            )}

            <AudienceDetailsSection
              icon={Link2}
              title={LL.campaignDetails.trackingTitle()}
              description={LL.campaignDetails.utmParametersAutomaticallyIncluded()}
              className="entry-fade-in"
              style={entryDelay(trackingDelay)}
            >
              <div className="grid auto-cols-max grid-flow-col grid-rows-2 place-items-start gap-x-8 text-sm max-md:grid-flow-row max-md:grid-rows-none max-md:grid-cols-1">
                <h3 className="text-sm font-medium text-muted-foreground">
                  utm_source
                </h3>
                <InlineCode>korzaapp</InlineCode>
                <h3 className="text-sm font-medium text-muted-foreground">
                  utm_medium
                </h3>
                <InlineCode>push</InlineCode>
                <h3 className="text-sm font-medium text-muted-foreground">
                  utm_campaign
                </h3>
                <InlineCode>{campaign.utmCampaign}</InlineCode>
              </div>
            </AudienceDetailsSection>

            {isMobile && (
              <CampaignDetailsActionBar
                campaignName={campaign.name}
                duplicateHref={duplicateHref}
                canCancel={canCancel}
                isCancelling={isCancelling}
                onCancelConfirm={onCancelConfirm}
              />
            )}
          </div>
        </CenteredContent>
      </CenteredContentLayout>
    </SidebarLayout>
  );
}
